import { pdfOptions } from 'ComponentLibrary/src/common/pdf'
import { Box, ButtonBase, ClickAwayListener, Grid, Popper, SelectChangeEvent, Stack, Tooltip } from '@mui/material'
import {
    ArrowSquareOutIcon,
    CaretDownIcon,
    CaretUpIcon,
    CheckCircleIcon,
    ClockIcon,
    FileTextIcon,
    MinusCircleIcon,
    SparkleIcon,
    UploadSimpleIcon,
    XCircleIcon,
} from '@phosphor-icons/react'
import { isNil } from 'lodash'
import { ChangeEvent, memo, Ref, useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'

import { tokens } from 'ComponentLibrary'
import { LoadingSkeleton } from '../../atoms/LoadingSkeleton'
import { Select } from '../../atoms/Select'
import { getFileExtension, whitelistedFiles } from '../../common/files'
import { TextButtonNew, TinyButton } from '../../piazza/buttons'
import { Typography } from '../../piazza/text'
import { UploadErrorAlert } from '../UploadErrorAlert'

const DEFAULT_PREVIEW_HEIGHT = 640
const DEFAULT_PDF_PAGE_WIDTH = 500
const DEFAULT_EXTRACTABLE_CONTENT_TYPES = ['application/pdf']
const DEFAULT_EXTRACTABLE_EXTENSIONS = ['pdf']
const PREVIEW_PADDING_PX = 12
const PREVIEW_PADDING = `${PREVIEW_PADDING_PX}px`
const PREVIEW_END_PADDING = tokens.SizingSpacing300
const PREVIEW_CONTAINER_PADDING = `${PREVIEW_PADDING} ${PREVIEW_END_PADDING} ${PREVIEW_END_PADDING} ${PREVIEW_PADDING}`
const documentPreviewOverlayElementSx = {
    bgcolor: tokens.ColorSurfaceDefault,
    borderRadius: tokens.SizingCornerS,
    boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.12)',
}
const documentPreviewSelectSx = {
    ...documentPreviewOverlayElementSx,
    flex: 1,
    minWidth: 0,
    borderColor: tokens.ColorBorderDefault,
    borderWidth: 1,
}

export type DocumentExtractionPanelDocument = {
    id: string
    name: string
    url: string
    contentType?: string
}

export type DocumentExtractionStepStatus =
    | 'inactive'
    | 'pending'
    | 'running'
    | 'retrying'
    | 'completed'
    | 'not_found'
    | 'failed'
    | 'skipped'

export type DocumentExtractionProgress = {
    steps: {
        id: string
        label: { de: string; en: string }
        description: { de: string; en: string }
        status: Exclude<DocumentExtractionStepStatus, 'inactive'>
        completed_units?: number | null
        total_units?: number | null
        unit?: string | null
    }[]
    warnings: {
        warning_type: string
        message: { de: string; en: string }
    }[]
}

export type DocumentExtractionStep = {
    id: string
    title: string
    status: DocumentExtractionStepStatus
    description?: string
    completedUnits?: number | null
    totalUnits?: number | null
    unit?: string | null
}

type DocumentExtractionPanelProps = {
    documents: DocumentExtractionPanelDocument[]
    selectedDocument?: string
    onSelectDocument: (documentId: string | undefined) => void
    selectedDocumentFile?: Blob
    isSelectedDocumentLoading?: boolean
    onUploadDocuments?: (files: File[]) => void
    isUploadingDocuments?: boolean
    isUploadDisabled?: boolean
    uploadDisabledTooltip?: string
    failedDocumentUploads?: string[]
    onExtractDocument?: (props: { document: DocumentExtractionPanelDocument; fileData: Blob }) => Promise<void> | void
    isExtractingDocument?: boolean
    extractionSteps?: DocumentExtractionStep[]
    progress?: DocumentExtractionProgress
    locale?: string
    title?: string
    uploadButtonLabel?: string
    emptyDocumentsMessage?: string
    noSelectedDocumentMessage?: string
    unsupportedPreviewMessage?: string
    openDocumentButtonLabel?: string
    extractDocumentButtonLabel?: string
    extractDocumentLoadingLabel?: string
    extractDocumentTooltip?: string
    extractionStepsTitle?: string
    extractionNotes?: string[]
    acceptedFileTypes?: string
    extractableContentTypes?: string[]
    extractableExtensions?: string[]
    previewHeight?: number
    pdfPageWidth?: number
    onOpenDocument?: (document: DocumentExtractionPanelDocument) => void
}

type ExtractionStepStatusIconProps = {
    status: DocumentExtractionStepStatus
}

type ExtractionStepsFlyoutProps = {
    anchorEl: HTMLButtonElement | null
    extractionNotes: ExtractionNote[]
    extractionSteps: DocumentExtractionStep[]
    extractionStepsTitle: string
    open: boolean
    onClose: () => void
}

type DocumentPreviewProps = {
    activeDocument?: DocumentExtractionPanelDocument
    emptyStateMessage: string
    isSelectedDocumentLoading: boolean
    pdfPageWidth: number
    previewHeight: number
    selectedDocumentFile?: Blob
    unsupportedPreviewMessage: string
}

type ExtractionSummaryProps = {
    buttonRef: Ref<HTMLButtonElement>
    finishedStepsCount: number
    isExtracting: boolean
    extractionNotes: ExtractionNote[]
    extractionStepsCount: number
    extractionStepsTitle: string
    isOpen: boolean
    onClick: () => void
}

type PdfPreviewProps = {
    documentId: string
    fileData: Blob
    pdfPageWidth: number
}

type ImagePreviewProps = {
    alt: string
    fileData: Blob
}

type ExtractionNote = {
    id: string
    text: string
}

const ExtractionStepStatusIcon = ({ status }: ExtractionStepStatusIconProps) => {
    if (status === 'running' || status === 'retrying') {
        return (
            <Box
                role="img"
                aria-label={status}
                sx={{
                    width: tokens.SizingIconS,
                    height: tokens.SizingIconS,
                    borderRadius: '50%',
                    border: `2px solid ${tokens.ColorIconBoldest}`,
                    borderTopColor: 'transparent',
                    animation: 'spin 1s linear infinite',
                    flexShrink: 0,
                    '@keyframes spin': {
                        from: { transform: 'rotate(0deg)' },
                        to: { transform: 'rotate(360deg)' },
                    },
                }}
            />
        )
    }

    if (status === 'completed') {
        return (
            <CheckCircleIcon
                aria-label={status}
                size={tokens.SizingIconS}
                color={tokens.ColorIconPositive}
                weight="fill"
                style={{ flexShrink: 0 }}
            />
        )
    }

    if (status === 'failed') {
        return (
            <XCircleIcon
                aria-label={status}
                size={tokens.SizingIconS}
                color={tokens.ColorIconDanger}
                weight="fill"
                style={{ flexShrink: 0 }}
            />
        )
    }

    if (status === 'not_found' || status === 'skipped') {
        return (
            <MinusCircleIcon
                aria-label={status}
                size={tokens.SizingIconS}
                color={tokens.ColorIconSubtle}
                weight="bold"
                style={{ flexShrink: 0 }}
            />
        )
    }

    return (
        <ClockIcon aria-label={status} size={tokens.SizingIconS} color={tokens.ColorIconDisabled} weight="bold" style={{ flexShrink: 0 }} />
    )
}

const isFinishedStep = ({ status }: DocumentExtractionStep) =>
    status === 'completed' || status === 'not_found' || status === 'failed' || status === 'skipped'

const ExtractionStepsFlyout = ({
    anchorEl,
    extractionNotes,
    extractionSteps,
    extractionStepsTitle,
    open,
    onClose,
}: ExtractionStepsFlyoutProps) => {
    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-end"
            modifiers={[
                {
                    name: 'offset',
                    options: {
                        offset: [0, 8],
                    },
                },
                {
                    name: 'preventOverflow',
                    options: {
                        padding: 16,
                    },
                },
            ]}
            sx={{
                zIndex: 1300,
            }}
        >
            <ClickAwayListener onClickAway={onClose}>
                <Box
                    sx={{
                        width: '420px',
                        maxWidth: 'calc(100vw - 32px)',
                        padding: tokens.SizingSpacing200,
                        borderRadius: tokens.SizingCornerM,
                        backgroundColor: tokens.ColorSurfaceDefault,
                        ...tokens.Flyout,
                    }}
                >
                    <Stack direction="column" gap={tokens.SizingSpacing100}>
                        <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                            {extractionStepsTitle}
                        </Typography>
                        <Stack direction="column" gap="0" width="100%">
                            {extractionSteps.map((step) => (
                                <Box
                                    key={step.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: tokens.SizingSpacing100,
                                        padding: `${tokens.SizingSpacing075} ${tokens.SizingSpacing100}`,
                                        borderRadius: tokens.SizingCornerS,
                                        backgroundColor: tokens.ColorSurfaceDefault,
                                    }}
                                >
                                    <Box sx={{ display: 'inline-flex', flexShrink: 0 }}>
                                        <ExtractionStepStatusIcon status={step.status} />
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="textS" color={tokens.ColorTextBold}>
                                            {step.title}
                                        </Typography>
                                        {step.description && (
                                            <Typography variant="textXS" color={tokens.ColorTextDefault}>
                                                {step.description}
                                            </Typography>
                                        )}
                                        {!isNil(step.completedUnits) && !isNil(step.totalUnits) && (
                                            <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                                                {step.completedUnits}/{step.totalUnits}
                                                {step.unit ? ` ${step.unit}` : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                        {extractionNotes.length > 0 && (
                            <Stack direction="column" gap={tokens.SizingSpacing050} paddingTop={tokens.SizingSpacing100}>
                                {extractionNotes.map((note) => (
                                    <Typography key={note.id} variant="textXS" color={tokens.ColorTextDefault}>
                                        {note.text}
                                    </Typography>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </Box>
            </ClickAwayListener>
        </Popper>
    )
}

const ExtractionSummary = ({
    buttonRef,
    finishedStepsCount,
    isExtracting,
    extractionNotes,
    extractionStepsCount,
    extractionStepsTitle,
    isOpen,
    onClick,
}: ExtractionSummaryProps) => {
    return (
        <ButtonBase
            ref={buttonRef}
            disableRipple
            onClick={onClick}
            sx={{
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                textAlign: 'left',
                borderRadius: tokens.SizingCornerS,
                padding: `${tokens.SizingSpacing075} ${tokens.SizingSpacing100}`,
                gap: tokens.SizingSpacing100,
                '&:hover': {
                    backgroundColor: tokens.ColorSurfaceHover,
                },
                '&:active': {
                    backgroundColor: tokens.ColorSurfacePressed,
                },
            }}
        >
            <Stack direction="column" gap={tokens.SizingSpacing025} width="100%" minWidth={0}>
                <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing050} flexWrap="wrap" minWidth={0}>
                    <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                        {extractionStepsTitle}
                    </Typography>
                    <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                        {finishedStepsCount}/{extractionStepsCount}
                    </Typography>
                </Stack>
                {extractionNotes.map((note) => (
                    <Typography key={note.id} variant="textXS" color={tokens.ColorTextSubtle}>
                        {note.text}
                    </Typography>
                ))}
            </Stack>
            <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100} flexShrink={0}>
                {!isOpen && isExtracting && <ExtractionStepStatusIcon status="running" />}
                {isOpen ? (
                    <CaretUpIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconSubtle} />
                ) : (
                    <CaretDownIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconSubtle} />
                )}
            </Stack>
        </ButtonBase>
    )
}

const PdfPreview = memo(function PdfPreview({ documentId, fileData, pdfPageWidth }: PdfPreviewProps) {
    const previewContentRef = useRef<HTMLDivElement>(null)
    const [numPages, setNumPages] = useState<number | null>(null)
    const [previewWidth, setPreviewWidth] = useState<number>()

    const pageNumbers = useMemo(() => {
        if (!numPages) {
            return [1]
        }

        return Array.from({ length: numPages }, (_value, index) => index + 1)
    }, [numPages])

    useEffect(() => {
        setNumPages(null)
    }, [documentId])

    useEffect(() => {
        const previewElement = previewContentRef.current
        if (!previewElement) {
            setPreviewWidth(undefined)
            return
        }

        const updatePreviewWidth = () => {
            const nextWidth = Math.floor(previewElement.clientWidth - 2 * PREVIEW_PADDING_PX)
            setPreviewWidth(nextWidth > 0 ? nextWidth : undefined)
        }

        updatePreviewWidth()

        if (typeof ResizeObserver === 'undefined') {
            return
        }

        const observer = new ResizeObserver(updatePreviewWidth)
        observer.observe(previewElement)

        return () => {
            observer.disconnect()
        }
    }, [documentId])

    return (
        <Stack direction="column" width="100%" height="100%" minHeight={0} gap={tokens.SizingSpacing100} ref={previewContentRef}>
            <Box display="flex" justifyContent="center" alignItems="flex-start" flex={1} minHeight={0} overflow="auto">
                <Document
                    options={pdfOptions}
                    file={fileData}
                    onLoadSuccess={({ numPages: loadedNumPages }) => setNumPages(loadedNumPages)}
                >
                    <Stack direction="column" gap={tokens.SizingSpacing100}>
                        {pageNumbers.map((pageNumber) => (
                            <Page
                                key={`${documentId}-${pageNumber}`}
                                pageNumber={pageNumber}
                                width={previewWidth ?? pdfPageWidth}
                                renderAnnotationLayer={false}
                                renderTextLayer={false}
                            />
                        ))}
                    </Stack>
                </Document>
            </Box>
        </Stack>
    )
})

const ImagePreview = ({ alt, fileData }: ImagePreviewProps) => {
    const previewUrl = useMemo(() => URL.createObjectURL(fileData), [fileData])

    useEffect(() => {
        return () => {
            URL.revokeObjectURL(previewUrl)
        }
    }, [previewUrl])

    return (
        <Box
            component="img"
            src={previewUrl}
            alt={alt}
            sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                boxSizing: 'border-box',
            }}
        />
    )
}

const normalizeContentType = (contentType?: string) => contentType?.split(';', 1)[0]?.trim().toLowerCase()

const isImageContentType = (contentType?: string) => Boolean(contentType?.startsWith('image/'))

const renderPreviewFallback = (message: string, previewHeight: number) => {
    return (
        <Box
            height="100%"
            minHeight={`${previewHeight}px`}
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={tokens.SizingSpacing200}
        >
            <Typography variant="textS" color={tokens.ColorTextSubtle}>
                {message}
            </Typography>
        </Box>
    )
}

const DocumentPreview = ({
    activeDocument,
    emptyStateMessage,
    isSelectedDocumentLoading,
    pdfPageWidth,
    previewHeight,
    selectedDocumentFile,
    unsupportedPreviewMessage,
}: DocumentPreviewProps) => {
    if (!activeDocument) {
        return renderPreviewFallback(emptyStateMessage, previewHeight)
    }

    if (isSelectedDocumentLoading) {
        return <LoadingSkeleton width="100%" height="100%" sx={{ minHeight: `${previewHeight}px` }} />
    }

    const contentType = selectedDocumentFile?.type || activeDocument.contentType
    const normalizedContentType = normalizeContentType(contentType)

    if (normalizedContentType === 'application/pdf' && selectedDocumentFile) {
        return <PdfPreview documentId={activeDocument.id} fileData={selectedDocumentFile} pdfPageWidth={pdfPageWidth} />
    }

    if (isImageContentType(contentType) && selectedDocumentFile) {
        return <ImagePreview alt={activeDocument.name} fileData={selectedDocumentFile} />
    }

    if (normalizedContentType === 'application/pdf' || isImageContentType(contentType)) {
        return null
    }

    return (
        <Stack
            direction="column"
            height="100%"
            alignItems="center"
            justifyContent="center"
            minHeight={`${previewHeight}px`}
            gap={tokens.SizingSpacing100}
            padding={tokens.SizingSpacing200}
        >
            <FileTextIcon size={tokens.SizingIconL} color={tokens.ColorIconSubtle} />
            <Typography variant="textS" color={tokens.ColorTextSubtle}>
                {unsupportedPreviewMessage}
            </Typography>
        </Stack>
    )
}

const getEmptyStateMessage = ({
    documents,
    emptyDocumentsMessage,
    noSelectedDocumentMessage,
}: Pick<DocumentExtractionPanelProps, 'documents' | 'emptyDocumentsMessage' | 'noSelectedDocumentMessage'>) => {
    return documents.length === 0
        ? (emptyDocumentsMessage ?? 'Es wurden noch keine Dokumente hochgeladen.')
        : (noSelectedDocumentMessage ?? 'Kein Dokument ausgewählt.')
}

export const DocumentExtractionPanel = ({
    documents,
    selectedDocument,
    onSelectDocument,
    selectedDocumentFile,
    isSelectedDocumentLoading = false,
    onUploadDocuments,
    isUploadingDocuments = false,
    isUploadDisabled = false,
    uploadDisabledTooltip,
    failedDocumentUploads = [],
    onExtractDocument,
    isExtractingDocument = false,
    extractionSteps = [],
    progress,
    locale = 'de-DE',
    title = 'Werte aus Dokument auslesen',
    uploadButtonLabel = 'Hochladen',
    emptyDocumentsMessage = 'Es wurden noch keine Dokumente hochgeladen.',
    noSelectedDocumentMessage = 'Kein Dokument ausgewählt.',
    unsupportedPreviewMessage = 'Für diesen Dateityp ist keine Vorschau verfügbar.',
    openDocumentButtonLabel = 'Datei öffnen',
    extractDocumentButtonLabel = 'Auslese starten',
    extractDocumentLoadingLabel = 'Auslese läuft...',
    extractDocumentTooltip,
    extractionStepsTitle = 'Extraktionsschritte',
    extractionNotes = [],
    acceptedFileTypes = whitelistedFiles,
    extractableContentTypes = DEFAULT_EXTRACTABLE_CONTENT_TYPES,
    extractableExtensions = DEFAULT_EXTRACTABLE_EXTENSIONS,
    previewHeight = DEFAULT_PREVIEW_HEIGHT,
    pdfPageWidth = DEFAULT_PDF_PAGE_WIDTH,
    onOpenDocument,
}: DocumentExtractionPanelProps) => {
    const uploadDocumentInputRef = useRef<HTMLInputElement>(null)
    const [extractionStepsAnchorEl, setExtractionStepsAnchorEl] = useState<HTMLButtonElement | null>(null)
    const [isExtractionStepsPopoverOpen, setIsExtractionStepsPopoverOpen] = useState(false)
    const [shouldAutoCloseExtractionSteps, setShouldAutoCloseExtractionSteps] = useState(false)

    const activeDocument = useMemo(() => {
        if (!selectedDocument) {
            return undefined
        }

        return documents.find((document) => document.id === selectedDocument)
    }, [documents, selectedDocument])

    const documentOptions = useMemo(() => documents.map((document) => ({ value: document.id, label: document.name })), [documents])

    const normalizedExtractableContentTypes = useMemo(
        () =>
            new Set(extractableContentTypes.map(normalizeContentType).filter((contentType): contentType is string => Boolean(contentType))),
        [extractableContentTypes]
    )
    const normalizedExtractableExtensions = useMemo(
        () => new Set(extractableExtensions.map((extension) => extension.toLowerCase())),
        [extractableExtensions]
    )

    const selectedDocumentContentType = normalizeContentType(selectedDocumentFile?.type || activeDocument?.contentType)
    const selectedDocumentExtension = activeDocument ? getFileExtension(activeDocument.name)?.toLowerCase() : undefined
    const isExtractableDocumentSelected = Boolean(
        activeDocument &&
        ((selectedDocumentContentType && normalizedExtractableContentTypes.has(selectedDocumentContentType)) ||
            (selectedDocumentExtension && normalizedExtractableExtensions.has(selectedDocumentExtension)))
    )

    const hasUploadAction = Boolean(onUploadDocuments)
    const hasExtractionAction = Boolean(onExtractDocument)
    const usesEnglishLocale = locale.toLowerCase().startsWith('en')
    const displayedExtractionSteps = useMemo<DocumentExtractionStep[]>(
        () =>
            progress
                ? progress.steps.map((step) => ({
                      id: step.id,
                      title: usesEnglishLocale ? step.label.en : step.label.de,
                      description: usesEnglishLocale ? step.description.en : step.description.de,
                      status: step.status,
                      completedUnits: step.completed_units,
                      totalUnits: step.total_units,
                      unit: step.unit,
                  }))
                : extractionSteps,
        [extractionSteps, progress, usesEnglishLocale]
    )
    const displayedExtractionNotes = useMemo<ExtractionNote[]>(
        () => [
            ...extractionNotes.map((note, index) => ({ id: `note-${index}`, text: note })),
            ...(progress?.warnings.map((warning) => ({
                id: `warning-${warning.warning_type}`,
                text: usesEnglishLocale ? warning.message.en : warning.message.de,
            })) ?? []),
        ],
        [extractionNotes, progress, usesEnglishLocale]
    )
    const hasExtractionProgress = isExtractingDocument || displayedExtractionSteps.length > 0 || Boolean(progress?.warnings.length)
    const isExtractionActive =
        isExtractingDocument ||
        displayedExtractionSteps.some(({ status }) => status === 'pending' || status === 'running' || status === 'retrying')
    const finishedStepsCount = displayedExtractionSteps.filter(isFinishedStep).length
    const extractionButtonLabel = isExtractionActive ? extractDocumentLoadingLabel : extractDocumentButtonLabel
    const emptyStateMessage = getEmptyStateMessage({
        documents,
        emptyDocumentsMessage,
        noSelectedDocumentMessage,
    })

    const openExtractionSteps = ({ shouldAutoClose = false }: { shouldAutoClose?: boolean } = {}) => {
        setShouldAutoCloseExtractionSteps(shouldAutoClose)
        setIsExtractionStepsPopoverOpen(true)
    }

    const handleDocumentChange = (event: SelectChangeEvent<string>) => {
        onSelectDocument(event.target.value || undefined)
    }

    const handleDocumentUpload = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files?.length) {
            onUploadDocuments?.(Array.from(event.target.files))
        }

        event.target.value = ''
    }

    const handleOpenExtractionSteps = () => {
        if (isExtractionStepsPopoverOpen) {
            handleCloseExtractionSteps()
            return
        }

        openExtractionSteps()
    }

    const handleCloseExtractionSteps = () => {
        setIsExtractionStepsPopoverOpen(false)
        setShouldAutoCloseExtractionSteps(false)
    }

    const handleExtractDocument = () => {
        if (!activeDocument || !selectedDocumentFile || !onExtractDocument || !isExtractableDocumentSelected) {
            return
        }

        openExtractionSteps({ shouldAutoClose: true })
        void onExtractDocument({ document: activeDocument, fileData: selectedDocumentFile })
    }

    const handleOpenDocument = () => {
        if (!activeDocument) {
            return
        }

        if (onOpenDocument) {
            onOpenDocument(activeDocument)
            return
        }

        const openedWindow = window.open('about:blank', '_blank')
        if (!openedWindow) {
            return
        }

        openedWindow.opener = null
        openedWindow.location.href = activeDocument.url
    }

    useEffect(() => {
        const areExtractionStepsFinished = displayedExtractionSteps.length > 0 && displayedExtractionSteps.every(isFinishedStep)

        if (shouldAutoCloseExtractionSteps && !isExtractingDocument && isExtractionStepsPopoverOpen && areExtractionStepsFinished) {
            handleCloseExtractionSteps()
        }
    }, [displayedExtractionSteps, isExtractingDocument, isExtractionStepsPopoverOpen, shouldAutoCloseExtractionSteps])

    return (
        <Stack direction="column" gap={tokens.SizingSpacing150} width="100%" height="100%">
            {hasUploadAction && (
                <input
                    ref={uploadDocumentInputRef}
                    hidden
                    multiple
                    onChange={handleDocumentUpload}
                    type="file"
                    accept={acceptedFileTypes}
                />
            )}
            {failedDocumentUploads.length > 0 && <UploadErrorAlert failedUploads={failedDocumentUploads} />}
            <Box width="100%" flex={1} minHeight={0}>
                <Stack direction="column" width="100%" height="100%" gap={tokens.SizingSpacing200}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={tokens.SizingSpacing100}>
                        <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                            {title}
                        </Typography>
                        <Stack direction="row" gap={tokens.SizingSpacing050} flexWrap="wrap" justifyContent="flex-end">
                            {hasExtractionAction && (
                                <Tooltip title={extractDocumentTooltip}>
                                    <span>
                                        <TextButtonNew
                                            label={extractionButtonLabel}
                                            leftIcon={<SparkleIcon />}
                                            onClick={handleExtractDocument}
                                            disabled={
                                                isExtractionActive ||
                                                !activeDocument ||
                                                isSelectedDocumentLoading ||
                                                !isExtractableDocumentSelected
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            )}
                        </Stack>
                    </Stack>
                    <Grid container spacing={tokens.SizingSpacing200}>
                        <Grid size={{ xs: 12, md: 6 }} alignContent="center">
                            {hasExtractionProgress ? (
                                <>
                                    <ExtractionSummary
                                        buttonRef={setExtractionStepsAnchorEl}
                                        finishedStepsCount={finishedStepsCount}
                                        isExtracting={isExtractionActive}
                                        extractionNotes={displayedExtractionNotes}
                                        extractionStepsCount={displayedExtractionSteps.length}
                                        extractionStepsTitle={extractionStepsTitle}
                                        isOpen={isExtractionStepsPopoverOpen}
                                        onClick={handleOpenExtractionSteps}
                                    />
                                    <ExtractionStepsFlyout
                                        anchorEl={extractionStepsAnchorEl}
                                        extractionNotes={displayedExtractionNotes}
                                        extractionSteps={displayedExtractionSteps}
                                        extractionStepsTitle={extractionStepsTitle}
                                        open={isExtractionStepsPopoverOpen && extractionStepsAnchorEl !== null}
                                        onClose={handleCloseExtractionSteps}
                                    />
                                </>
                            ) : (
                                <Box minHeight="100%" />
                            )}
                        </Grid>
                    </Grid>
                    <Box
                        flex={1}
                        minHeight={`${previewHeight}px`}
                        display="flex"
                        flexDirection="column"
                        overflow="hidden"
                        position="relative"
                        border={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
                        borderRadius={tokens.SizingCornerM}
                        bgcolor={tokens.ColorSurfaceDefault}
                    >
                        <Box
                            flex={1}
                            minHeight={0}
                            overflow="auto"
                            display="flex"
                            alignContent="center"
                            justifyContent="center"
                            padding={PREVIEW_CONTAINER_PADDING}
                        >
                            <Stack
                                position="absolute"
                                top={PREVIEW_PADDING}
                                right={PREVIEW_END_PADDING}
                                left={PREVIEW_PADDING}
                                zIndex={1}
                                direction="row"
                                alignItems="center"
                                gap={tokens.SizingSpacing100}
                            >
                                <Select<string, string>
                                    inputLabel="Dokument"
                                    value={activeDocument?.id ?? ''}
                                    onChange={handleDocumentChange}
                                    options={documentOptions}
                                    clear={activeDocument ? () => onSelectDocument(undefined) : undefined}
                                    formControlSx={documentPreviewSelectSx}
                                    disabled={documents.length === 0 || isExtractingDocument}
                                />
                                <Stack direction="row" spacing={tokens.SizingSpacing100} flexShrink={0}>
                                    <TinyButton
                                        label={openDocumentButtonLabel}
                                        leftIcon={<ArrowSquareOutIcon />}
                                        onClick={handleOpenDocument}
                                        sx={documentPreviewOverlayElementSx}
                                        disabled={!activeDocument}
                                    />
                                    {hasUploadAction && (
                                        <TinyButton
                                            label={uploadButtonLabel}
                                            leftIcon={<UploadSimpleIcon />}
                                            onClick={() => uploadDocumentInputRef.current?.click()}
                                            disabled={isUploadDisabled || isUploadingDocuments || isExtractingDocument}
                                            tooltip={isUploadDisabled ? uploadDisabledTooltip : undefined}
                                            sx={documentPreviewOverlayElementSx}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                            <DocumentPreview
                                activeDocument={activeDocument}
                                emptyStateMessage={emptyStateMessage}
                                isSelectedDocumentLoading={isSelectedDocumentLoading}
                                pdfPageWidth={pdfPageWidth}
                                previewHeight={previewHeight}
                                selectedDocumentFile={selectedDocumentFile}
                                unsupportedPreviewMessage={unsupportedPreviewMessage}
                            />
                        </Box>
                    </Box>
                </Stack>
            </Box>
        </Stack>
    )
}
